












































import Vue, { PropType } from 'vue'
import ChannelAssignment from '@/types/ChannelAssignment.interface'
import { mapGetters } from 'vuex'
import Invitation from '@/types/Invitation.interface'
import { randomKey } from '@/utility/helpers'
import User from '@/types/User.interface'
import Channel from '@/types/Channel.interface'
export default Vue.extend({
    props: {
        //  The channel we're assigning someone to
        channel: {
            type: Object as PropType<Channel>,
            required: true
        },
        //  Applies the compact style
        compact: {
            type: Boolean,
            default: false
        },
        //  Applies a max-width to the component
        maxWidth: {
            type: String,
            default: null
        },
        //  Applies a min-width to the component
        minWidth: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            //assignmentOptions: null,
            selected: null,
            selectedKey: randomKey(),
            tooltip: 'Assign a user to the conversation'
        }
    },
    mounted() {
        //  Set our selected option
        this.setSelected()
    },
    computed: {
        ...mapGetters(['isApp', 'invitations', 'user', 'users']),
        //  Determine the 'assigned' status of the channel
        assigned(): number {
            if (!this.user) return null

            //  If the channel is assigned to auth user, return 1
            if (this.channel.assigned_id === this.user.id) return 1

            //  Else, if channel assigned_id is null, return 0
            if (!this.channel.assigned_id) return 0

            //  Else, channel assigned_id exists, it's assigned to someone else so return -1
            return -1
        },
        //  The ChannelAssignment options available to our select
        assignmentOptions(): object[] {
            //  Container for our options
            const options = [
                //  Our object for 'Me'
                {
                    display: 'Me',
                    value: this.user.id,
                    icon: 'mdi-account',
                    logo: this.user.logo_route ? this.user.logo_route.remote_link : null,
                    hidden: this.assigned < 1
                },
                //  Our object for 'Unassigned'
                {
                    display: 'Unassigned',
                    value: null,
                    icon: 'mdi-account',
                    hidden: this.assigned === 0
                },
                //  Our object for 'Assign to Myself'
                {
                    display: 'Assign to Myself',
                    value: this.user.id,
                    icon: 'mdi-account',
                    logo: this.user.logo_route ? this.user.logo_route.remote_link : null,
                    hidden: this.assigned != 0
                },
                //  Add user options
                ...this.userNames,
                {
                    display: 'Add Teammate',
                    value: 'invitationButton',
                    icon: 'bi-plus-lg',
                    hidden: false
                }
            ]

            //  Set our options
            return options
            //return options.filter((x) => !x.hidden)
        },
        invitationCount(): number {
            //  If no invitations, return 0
            if (!this.invitations) return 0

            //  Else count the total that aren't snapdesk/messagedesk/support
            return this.invitations.filter(
                (x: Invitation) =>
                    x.role_id !== 5 &&
                    !x.email.includes('@snapdesk.app') &&
                    !x.email.includes('@messagedesk.com')
            ).length
        },
        operator(): boolean {
            //  Grab our current role
            const role = this.$store.getters.userRole(this.$store.getters.user)

            //  Check if this role is an operator
            return window.getProps(role, ['name']) === 'Operator'
        },
        //  Applies the compact style if it is set
        theme(): string {
            if (this.compact) return 'compact'
        },
        userCount(): number {
            return this.$store.getters.userCount
        },
        userLimit(): number {
            //  Grab our total number of users from our stripe plan
            return this.$store.getters.getLimit('user')
        },
        //  Convert the list of Users into  a list of assignment options
        userNames(): ChannelAssignment[] {
            //  If there are no users, return empty array
            if (!this.users) return []

            //  If you are an operator, return empty array
            if (this.operator && this.assigned === 0) return []

            //  Else, create container for names
            const names = []

            //  iterate through each user and grab their name
            for (let i = 0; i < this.users.length; i++) {
                names[i] = this.convertUserToAssignment(this.users[i])
            }

            //  Return the names
            return names
        },
        userLimitReached(): boolean {
            return this.userCount >= this.userLimit
        }
    },
    methods: {
        //  Convert a user into a ChannelAssignment object
        convertUserToAssignment(user: User): ChannelAssignment {
            //  Assemble the user assignment
            const option: ChannelAssignment = {
                display: this.$options.filters.userFullNameOrEmail(user),
                value: user.id,
                icon: 'mdi-account',
                logo: user.logo_route ? user.logo_route.remote_link : null,
                hidden: false
            }

            return option
        },
        //  Handle the optionSelected event
        onOptionSelected(userId: number | string | null): void {
            //  If userId is 'invitationButton'
            if (userId === 'invitationButton') {
                //  Pop invitation form
                return this.popNewInvitation()
            }

            //  Send the selected assignment to the parent
            this.$emit('assignmentSelected', userId)

            //  Make sure we update the selected
            this.setSelected()
        },
        popNewInvitation(): void {
            //  Proceed
            this.popNewUser()

            //  Set selected (sets selected to previously assigned value)
            this.setSelected()

            //  Renew key on component to force update UI
            this.selectedKey = randomKey()
        },
        popNewUser(): void {
            this.$store.dispatch('dialogOpen', {
                type: 'APP',
                title: 'Invite a Teammate',
                icon: 'mdi-account',
                color: 'primary',
                component: 'invitations-form',
                closeable: true,
                closeOnSubmit: true,
                class: 'pa-2',
                props: {
                    dialog: true
                }
            })
        },
        //  Set the assigned user
        setSelected(): void {
            //  Create a default selected item
            let selected = null

            //  If the channel is assigned to the authenticated user, selected is the 'Me' option
            if (this.assigned === 1 && this.user) {
                selected = this.convertUserToAssignment(this.user)
            }

            //  If the channel is Unassigned, selected is the 'Unassigned' option
            if (this.assigned === 0) {
                selected = this.assignmentOptions.find((x) => x.value === null)
            }

            //  If the channel is Assigned, selected is the assigned user
            if (this.assigned === -1) {
                const user = this.users.find((x) => x.id == this.channel.assigned_id)
                selected = this.convertUserToAssignment(user)
            }

            //  Set the selected channel assignment
            this.selected = selected
        }
    },
    watch: {
        channel(): void {
            //  Anytime our channel changes, we want to update the selected option
            this.setSelected()
        }
    }
})
